// src/pages/Tenant_Bootstrap.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Database,
  CalendarDays,
  Users as UsersIcon,
  AtSign,
  MapPin,
  Phone,
  User as UserIcon,
  KeyRound,
  Info,
  Boxes,
  BadgeCheck,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com/api";

function strongPwd(p) {
  if (!p || p.length < 8) return false;
  const sets = [
    /[a-z]/.test(p),
    /[A-Z]/.test(p),
    /[0-9]/.test(p),
    /[^A-Za-z0-9]/.test(p),
  ].filter(Boolean).length;
  return sets >= 3;
}

function getProductLabelFromDb(dbName) {
  const db = String(dbName || "").toLowerCase();

  if (db.startsWith("patrolv_t_")) return "Patrol Visit";
  if (db.startsWith("patrol_t_")) return "EBD Patrol";
  if (db.startsWith("sentinel_t_")) return "Sentinel";

  return "Unknown";
}

export default function Tenant_Bootstrap() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [numUsers, setNumUsers] = useState(5);
  const [numLicenses, setNumLicenses] = useState(0);

  const selected = useMemo(
    () => tenants.find((t) => t.TenantId === Number(selectedId)) || null,
    [tenants, selectedId]
  );

  const selectedProductLabel = useMemo(
    () => (selected ? getProductLabelFromDb(selected.DatabaseName) : "Unknown"),
    [selected]
  );

  const [companyName, setCompanyName] = useState("");
  const [adminFullName, setAdminFullName] = useState("Administrator");
  const [adminUsername, setAdminUsername] = useState("admin");
  const [adminPassword, setAdminPassword] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoPath, setLogoPath] = useState("");
  const urlRef = useRef(null);

  const [numClients, setNumClients] = useState(0);
  const [userMap, setUserMap] = useState("google");

  // NEW: Payroll Enabled
  const [payrollEnabled, setPayrollEnabled] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/tenants`);
        setTenants(r.data || []);
        if (id) setSelectedId(Number(id));
      } catch {
        Swal.fire("Error", "Unable to load tenants", "error");
      }
    })();
  }, [id]);

  useEffect(() => {
    if (selected && !companyName) {
      setCompanyName(selected.Name || selected.TenantCode);
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  async function uploadLogoIfAny(tenantId, username) {
    if (!logoFile) return null;

    const fd = new FormData();
    fd.append("tenantId", tenantId);
    fd.append("username", username || adminUsername || "admin");
    fd.append("file", logoFile);

    const r = await axios.post(`${API}/tenant/mysql/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return r.data?.logoPath || null;
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (!selected) {
      return Swal.fire("Validation", "Please choose a tenant", "warning");
    }

    if (!companyName.trim()) {
      return Swal.fire("Validation", "Company name is required", "warning");
    }

    if (!adminUsername.trim()) {
      return Swal.fire("Validation", "Admin username is required", "warning");
    }

    if (!strongPwd(adminPassword)) {
      return Swal.fire(
        "Weak password",
        "Password must be at least 8 chars and include 3 of: lowercase, uppercase, digit, symbol.",
        "warning"
      );
    }

    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      return Swal.fire("Validation", "Invalid email", "warning");
    }

    setSubmitting(true);

    try {
      let uploadedLogo = null;

      if (logoFile) {
        uploadedLogo = await uploadLogoIfAny(selected.TenantId, adminUsername);
        setLogoPath(uploadedLogo || "");
      }

      await axios.post(`${API}/tenant/mysql/bootstrap`, {
        tenantId: selected.TenantId,
        companyName: companyName.trim(),
        adminFullName: adminFullName.trim() || "Administrator",
        adminUsername: adminUsername.trim(),
        adminPassword,
        email: email.trim() || null,
        address: address.trim() || null,
        phone: phone.trim() || null,
        numUsers: Number(numUsers),
        numClients: Number(numClients),
        numLicenses: Number(numLicenses),
        userMap: userMap || null,

        // NEW: backend stores true as 1 and false as 0
        payrollEnabled,

        logo: uploadedLogo || null,
      });

      await Swal.fire(
        "Success",
        "Tenant bootstrapped. Admin user created.",
        "success"
      );

      navigate("/superAdmin/admin_Menu");
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Bootstrap failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setSubmitting(false);
    }
  }

  const statusPill = (s) => {
    if (s === "ACTIVE") {
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    }

    if (s === "BLOCKED") {
      return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
    }

    return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Building2 className="h-5 w-5" />
                  </div>

                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Bootstrap Tenant (Company + Admin)
                    </h1>
                    <p className="text-white/80 text-sm">
                      Pick a tenant, review license, then create company & admin
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-white ring-1 ring-white/30 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>

                  <div className="text-sm text-white/90">
                    <Time />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <BadgeCheck className="h-4 w-4" /> Validate & Create
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Boxes className="h-4 w-4" /> Module Access
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-1 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3">
                  <Building2 className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-800">
                    Select Tenant
                  </h3>
                </div>

                <div className="p-4 space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Tenant *
                  </label>

                  <select
                    value={selectedId || ""}
                    onChange={(e) =>
                      setSelectedId(Number(e.target.value) || null)
                    }
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    disabled={submitting}
                  >
                    <option value="">-- choose --</option>
                    {tenants.map((t) => (
                      <option key={t.TenantId} value={t.TenantId}>
                        {t.TenantCode} — {t.Name}
                      </option>
                    ))}
                  </select>

                  {selected ? (
                    <div className="mt-3 grid grid-cols-1 gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Database</div>

                        <div className="text-sm font-medium text-slate-800">
                          <span className="inline-flex items-center gap-2">
                            <Database className="h-4 w-4 text-slate-500" />
                            {selected.DatabaseName}
                          </span>
                        </div>

                        <div className="mt-1">
                          {(() => {
                            const label = selectedProductLabel;

                            const cls =
                              label === "EBD Patrol"
                                ? "bg-indigo-50 text-indigo-700 ring-indigo-200"
                                : label === "Patrol Visit"
                                ? "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200"
                                : label === "Sentinel"
                                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                : "bg-slate-100 text-slate-700 ring-slate-200";

                            return (
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${cls}`}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">
                          Plan / Max / Expires
                        </div>
                        <div className="text-sm font-medium text-slate-800">
                          {selected.PlanCode ?? "-"} •{" "}
                          {selected.MaxUsers ?? "-"} users •{" "}
                          {selected.ExpiresOn ?? "-"}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Status</div>

                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusPill(
                              selected.EffectiveStatus
                            )}`}
                          >
                            {selected.EffectiveStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <form
                className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white shadow-sm"
                onSubmit={onSubmit}
              >
                <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-cyan-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                      Company & Admin Details
                    </h3>
                  </div>

                  <div className="text-xs text-slate-600 flex items-center gap-1">
                    <Info className="h-3.5 w-3.5" />
                    Strong passwords required
                  </div>
                </div>

                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Company Name *
                    </label>

                    <div className="relative">
                      <Building2 className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g. Omega Tech Pvt Ltd"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Full Name
                    </label>

                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        value={adminFullName}
                        onChange={(e) => setAdminFullName(e.target.value)}
                        placeholder="Administrator"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Username *
                    </label>

                    <input
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      placeholder="admin"
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Password *
                    </label>

                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="password"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="StrongP@ssw0rd!"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Must be at least 8 chars and include 3 of: lowercase,
                      uppercase, digit, symbol.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Phone
                    </label>

                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 90000 00000"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Email (optional)
                    </label>

                    <div className="relative">
                      <AtSign className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@example.com"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Address
                    </label>

                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Company address"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      User Limit (num_users)
                    </label>

                    <div className="relative">
                      <Boxes className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="number"
                        min={1}
                        value={numUsers}
                        onChange={(e) =>
                          setNumUsers(Number(e.target.value) || 1)
                        }
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                        placeholder="e.g. 5"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Stored as <code>num_users</code> in the tenant DB.
                    </p>
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Visit Client Limit (num_clients)
                    </label>

                    <div className="relative">
                      <UsersIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="number"
                        min={0}
                        value={numClients}
                        onChange={(e) =>
                          setNumClients(
                            Number.isNaN(Number(e.target.value))
                              ? 0
                              : Number(e.target.value)
                          )
                        }
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                        placeholder="e.g. 50"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Used mainly for <strong>Patrol Visit</strong> tenants.
                      Stored as <code>num_clients</code>.
                    </p>
                  </div>

                  {selectedProductLabel === "Sentinel" && (
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Sentinel License Limit (num_licenses)
                      </label>

                      <div className="relative">
                        <Boxes className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type="number"
                          min={0}
                          value={numLicenses}
                          onChange={(e) =>
                            setNumLicenses(Number(e.target.value) || 0)
                          }
                          disabled={submitting}
                          className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                          placeholder="e.g. 25"
                        />
                      </div>

                      <p className="mt-1 text-xs text-slate-500">
                        Used for <strong>Sentinel</strong> tenants. Stored as{" "}
                        <code>num_licenses</code>.
                      </p>
                    </div>
                  )}

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Map Provider (user_map)
                    </label>

                    <select
                      value={userMap}
                      onChange={(e) => setUserMap(e.target.value)}
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    >
                      <option value="google">Google Maps</option>
                      <option value="mapbox">Mapbox</option>
                      <option value="none">No Map / Basic</option>
                    </select>

                    <p className="mt-1 text-xs text-slate-500">
                      Choose cheaper option if customer can&apos;t afford Google
                      Maps.
                    </p>
                  </div>

                  {/* NEW: Payroll Enabled Field */}
                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Payroll Enabled
                    </label>

                    <select
                      value={payrollEnabled ? "1" : "0"}
                      onChange={(e) =>
                        setPayrollEnabled(e.target.value === "1")
                      }
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>

                    <p className="mt-1 text-xs text-slate-500">
                      Yes will be stored as <code>1</code>, No as{" "}
                      <code>0</code>.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Company Logo (PNG/JPG/WEBP up to 2MB)
                    </label>

                    <div className="flex items-start gap-4">
                      <div className="w-40 h-40 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : logoPath ? (
                          <img
                            src={logoPath}
                            alt="Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-slate-400">
                            No logo
                          </span>
                        )}
                      </div>

                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setLogoFile(f || null);

                            if (urlRef.current) {
                              URL.revokeObjectURL(urlRef.current);
                              urlRef.current = null;
                            }

                            if (f) {
                              const url = URL.createObjectURL(f);
                              urlRef.current = url;
                              setLogoPreview(url);
                            } else {
                              setLogoPreview("");
                            }
                          }}
                          disabled={submitting}
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                        />

                        <p className="mt-1 text-xs text-slate-500">
                          Tip: use a transparent PNG or WEBP around ~512×512 for
                          best results.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Tenant</div>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        {selected?.TenantCode || "—"}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Expires</div>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-slate-500" />
                        {selected?.ExpiresOn ?? "—"}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Max Users</div>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <UsersIcon className="h-4 w-4 text-slate-500" />
                        {selected?.MaxUsers ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t border-slate-200">
                  <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
                    <p className="text-xs text-slate-600">
                      Review tenant license & modules before creating the admin.
                    </p>

                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                      >
                        <BadgeCheck className="h-4 w-4" />
                        {submitting ? "Saving…" : "Create Admin & Company"}
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate("/superAdmin/admin_Menu")}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/30 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
} 